import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UserService } from 'app/shared/services/user.service';
import { take } from 'rxjs';
import { User } from 'app/shared/models/user';
import { DxDataGridComponent } from 'devextreme-angular';
import _ from 'lodash';
import { SubjectUserSecurity } from 'app/shared/models/subject';
import { UserSecurityDto } from 'app/shared/models/user-security-dto';
import { CategoryUserSecurity } from 'app/shared/models/lookup-category';

const userGroups = ['Me', 'Me and my manager', 'People in my team', 'Everyone'];

@Component({
  selector: 'app-user-selection-matrix',
  templateUrl: './user-selection-matrix.component.html',
  styleUrls: ['./user-selection-matrix.component.scss'],
})
export class UserSelectionMatrixComponent implements OnInit, AfterViewInit {
  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  @Input() set userSecurityDtoList(val: SubjectUserSecurity[] | CategoryUserSecurity[]) {
    this._userSecurityDtoList = val;
    if (val) {
      this.addGridItems();
    }
  }

  @Output() onUserSelectionChanged: EventEmitter<UserSecurityDto[]> = new EventEmitter();
  @Output() onHasChangesChanged: EventEmitter<boolean> = new EventEmitter();

  users: User[] = [];
  currentUser: User;
  currentUserId: number;
  _userSecurityDtoList: SubjectUserSecurity[] | CategoryUserSecurity[] = [];
  userSecurityGridItems: UserSecurityDto[] = [];
  _userSecurityGridItems: UserSecurityDto[] = [];

  constructor(private userService: UserService) {}

  ngOnInit(): void {
    if (!this.users?.length) {
      this.userService
        .getActiveUsers()
        .pipe(take(1))
        .subscribe((response: User[]) => {
          this.users = [...response];
        });
    }
  }

  addGridItems(): void {
    this.userSecurityGridItems = [];
    this._userSecurityGridItems = [];

    const userGroupsLength = userGroups.length;
    for (let i = 0; i < userGroupsLength; i++) {
      const userSecurityDto = new UserSecurityDto();
      userSecurityDto.key = i;
      userSecurityDto.label = userGroups[i];
      userSecurityDto.isNotUserGroup = false;
      this.userSecurityGridItems.push(userSecurityDto);
    }

    const usersLength = this.users?.length;
    const gridItemsToAdd: UserSecurityDto[] = [];
    for (let i = 0; i < usersLength; i++) {
      const user = this.users[i];
      const userSecurityForUser: SubjectUserSecurity | CategoryUserSecurity = (<any>this._userSecurityDtoList).find(
        (i) => i?.userId == user.id
      );
      const userSecurityDto = new UserSecurityDto();
      userSecurityDto.key = i + userGroupsLength;
      userSecurityDto.label = user?.firstName + ' ' + user?.lastName;
      userSecurityDto.canRead = userSecurityForUser?.restrictAccess ? userSecurityForUser?.canRead || false : true;
      userSecurityDto.canEdit = userSecurityForUser?.restrictAccess ? userSecurityForUser?.canEdit || false : true;
      userSecurityDto.canDelete = userSecurityForUser?.restrictAccess ? userSecurityForUser?.canDelete || false : true;
      userSecurityDto.userId = user?.id;
      gridItemsToAdd.push(userSecurityDto);
    }

    gridItemsToAdd.sort((a, b) => a.label.localeCompare(b.label));
    this.userSecurityGridItems.push(...gridItemsToAdd);
    this._userSecurityGridItems = _.cloneDeep(this.userSecurityGridItems);
  }

  ngAfterViewInit(): void {
    this.currentUser = this.userService.getCurrentUser();
    this.currentUserId = this.currentUser.id;
  }

  onCellClick(event: any): void {
    const data = event?.data;
    const dataField = event.column.dataField;
    const value = event.value;
    const changes = this.dataGrid.editing.changes;
    if (data && !data?.isNotUserGroup) {
      const userIds: number[] = [];
      switch (data.key) {
        case 0: // Me
          userIds.push(this.currentUserId);
          break;
        case 1: // Me and my manager
          userIds.push(this.currentUserId);
          const manager = this.users.find((user) => user.id === this.currentUser.managerId);
          if (manager) userIds.push(manager.id);
          break;
        case 2: // People in my team
          userIds.push(this.currentUserId);
          userIds.push(...this.users.filter((user) => user.managerId === this.currentUserId).map((i) => i.id));
          break;
        case 3: // Everyone
          userIds.push(...this.users.map((i) => i.id));
          break;
        default:
          break;
      }

      const userSecurityGridItemsLength = this.userSecurityGridItems?.length;
      for (let i = 0; i < userSecurityGridItemsLength; i++) {
        const userSecurityGirdItems = this.userSecurityGridItems[i];
        if (userIds.findIndex((i) => i === userSecurityGirdItems.userId) > -1) {
          changes.push({
            data: { [dataField]: value },
            key: i,
            type: 'update',
          });
          this.autoSelectPrivs(value, dataField, changes, i);
        } else if (value && userSecurityGirdItems.key !== data.key) {
          changes.push({
            data: { [dataField]: false },
            key: i,
            type: 'update',
          });
        } else if (userSecurityGirdItems.key === data.key) {
          this.autoSelectPrivs(value, dataField, changes, data.key);
        }
      }
    } else {
      this.autoSelectPrivs(value, dataField, changes, data.key);
    }

    this.dataGrid.instance.saveEditData().then(() => {
      this.onHasChangesChanged.emit(
        !_.isEqual(
          this.userSecurityGridItems.filter((i) => i.isNotUserGroup),
          this._userSecurityGridItems.filter((i) => i.isNotUserGroup)
        )
      );

      this.onUserSelectionChanged.emit(this.userSecurityGridItems.filter((i) => i.isNotUserGroup));
    });
  }

  autoSelectPrivs(value: string, dataField: string, changes: any, key: number): void {
    if (value) {
      if (dataField === 'canEdit' || dataField === 'canDelete') {
        changes.push({
          data: { canRead: true },
          key: key,
          type: 'update',
        });
      }

      if (dataField === 'canDelete') {
        changes.push({
          data: { canEdit: true },
          key: key,
          type: 'update',
        });
      }
    } else {
      if (dataField === 'canEdit' || dataField === 'canRead') {
        changes.push({
          data: { canDelete: false },
          key: key,
          type: 'update',
        });
      }

      if (dataField === 'canRead') {
        changes.push({
          data: { canEdit: false },
          key: key,
          type: 'update',
        });
      }
    }
  }

  getSelectedUsers(): User[] {
    return this.users.filter(
      (i) => this.userSecurityGridItems.filter((i) => i.hasAccess()).findIndex((j) => i.id == j.userId) > -1
    );
  }
}
